import Box from "@mui/material/Box";
import { lazy, Suspense } from "react";
import Stack from "@mui/material/Stack";
import { Theme } from "../../data/style/Theme";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import { getRecentProgress } from "../../api/exercises";
import useScreenSize from "../../context/ScreenContext";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { CenteredRow, MainText, SecondaryTitle } from "../../data/style/Style";

const Loader = lazy(() => import("./Loader"));
const NoDataDiv = lazy(() => import("./NoDataDiv"));
const MILE_CONVERT = 0.000621371192;
const FT_CONVERT = 3.2808399;

const Unit = ({ type }) => {
  const { settings } = useAuth();
  const { mobileDevice } = useScreenSize();
  return (
    <MainText fontSize={mobileDevice ? "0.8rem" : "1rem"}>
      {type === 3 ? (
        "min"
      ) : type === 4 ? (
        <>{settings.weight === 0 ? "kg" : "lb"}</>
      ) : (
        "reps"
      )}
    </MainText>
  );
};

const RecentExercise = ({ type }) => {
  const { user, settings } = useAuth();
  const { mobileDevice } = useScreenSize();

  const { data, isPending } = useQuery({
    queryKey: ["recent", "exercises", "category", type, "user", user.id],
    queryFn: ({ signal }) => getRecentProgress({ type, signal }),
  });

  return (
    <div>
      {!isPending ? (
        <Stack
          p={mobileDevice ? 0.5 : 1}
          minHeight={mobileDevice ? "7vh" : "100px"}
        >
          <CenteredRow
            sx={{ borderBottom: `1px solid ${Theme.palette.border}`, p: 1 }}
          >
            <Box width={"45%"}>
              <SecondaryTitle
                sx={{
                  color:
                    type === 3
                      ? Theme.palette.up + " !important"
                      : type === 2
                        ? Theme.palette.blue + " !important"
                        : Theme.palette.gold + " !important",
                }}
              >
                {type === 3
                  ? "Cardio"
                  : type === 2
                    ? "Gymnastics"
                    : "Weightlifting"}
              </SecondaryTitle>
            </Box>
            <Box width={"25%"}>
              <SecondaryTitle>From</SecondaryTitle>
            </Box>
            <Box width={"25%"}>
              <SecondaryTitle>To</SecondaryTitle>
            </Box>
          </CenteredRow>
          <Stack>
            {data ? (
              <>
                {data.map((item, index) => (
                  <CenteredRow
                    key={index}
                    width={"100%"}
                    sx={{
                      borderBottom: `1px solid ${Theme.palette.border}`,
                      p: 1,
                    }}
                  >
                    <Box width={"45%"}>
                      <MainText fontSize={mobileDevice && "0.8rem"}>
                        {type === 3 && (
                          <>
                            {settings.distance === 0 ? (
                              <>
                                {item.distance > 1000
                                  ? item.distance / 1000 + " km"
                                  : item.distance + " m"}
                              </>
                            ) : (
                              <>
                                {item.distance > 1000
                                  ? (item.distance * MILE_CONVERT).toFixed(2) +
                                    " mi"
                                  : (item.distance * FT_CONVERT).toFixed(2) +
                                    " ft"}
                              </>
                            )}
                          </>
                        )}{" "}
                        {item.exercise}
                      </MainText>
                    </Box>
                    <Stack direction={"row"} gap={0.5} width={"25%"}>
                      {item.from !== 0 ? (
                        <>
                          <MainText fontSize={mobileDevice && "0.8rem"}>
                            {item.from}
                          </MainText>
                          <Unit type={type} />
                        </>
                      ) : (
                        <MainText fontSize={mobileDevice && "0.8rem"}>
                          None
                        </MainText>
                      )}
                    </Stack>
                    <CenteredRow gap={0.5} width={"30%"}>
                      <MainText fontSize={mobileDevice && "0.8rem"}>
                        {item.to}
                      </MainText>
                      <Unit type={type} />
                      {item.percentage !== 0 && (
                        <>
                          {item.percentage < 0 ? (
                            <>
                              <ArrowDownwardIcon
                                sx={{
                                  fontSize: mobileDevice ? "0.6rem" : "0.7rem",
                                  color: type === 3 ? "#02c252" : "#e74c3c",
                                }}
                              />
                              <MainText
                                fontSize={mobileDevice ? "0.6rem" : "0.7rem"}
                                sx={{
                                  color:
                                    type === 3
                                      ? "#02c252 !important"
                                      : "#e74c3c !important",
                                }}
                              >
                                {Math.abs(item.percentage)}%
                              </MainText>
                            </>
                          ) : (
                            <>
                              <ArrowUpwardIcon
                                sx={{
                                  fontSize: mobileDevice ? "0.6rem" : "0.7rem",
                                  color: type !== 3 ? "#02c252" : "#e74c3c",
                                }}
                              />
                              <MainText
                                fontSize={mobileDevice ? "0.6rem" : "0.7rem"}
                                sx={{
                                  color:
                                    type !== 3
                                      ? "#02c252 !important"
                                      : "#e74c3c !important",
                                }}
                              >
                                {Math.abs(item.percentage)}%
                              </MainText>
                            </>
                          )}
                        </>
                      )}
                    </CenteredRow>
                  </CenteredRow>
                ))}
              </>
            ) : (
              <Suspense>
                <NoDataDiv minHeight={"45px"} text={"No entries yet."} />
              </Suspense>
            )}
          </Stack>
        </Stack>
      ) : (
        <Suspense>
          <Loader dark={true} minHeight={80} height={"12vh"} />
        </Suspense>
      )}
    </div>
  );
};

export default RecentExercise;
